import {
    Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges,
    ChangeDetectionStrategy, ChangeDetectorRef, Output, EventEmitter
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TimeTrackingService } from '../../services/time-tracking.service';
import { DataService } from '@app/shared/common/auth/data.service';
import { TimeEntry, ActiveTimer } from '../../models/time-tracking.models';

@Component({
    selector: 'vf-task-time-section',
    templateUrl: './task-time-section.component.html',
    styleUrls: ['./task-time-section.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskTimeSectionComponent implements OnInit, OnDestroy, OnChanges {

    @Input() taskId: number;
    @Input() taskName: string;
    @Input() projectId: number | null = null;
    @Output() openOverlay = new EventEmitter<void>();

    entries: TimeEntry[] = [];
    activeTimer: ActiveTimer | null = null;
    elapsed = '00:00';
    isLoading = false;
    isStopping = false;
    isExpanded = true;

    private subs = new Subscription();

    constructor(
        public ttService: TimeTrackingService,
        private dataService: DataService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.subs.add(
            this.ttService.activeTimer$.subscribe(timer => {
                this.activeTimer = timer;
                this.cdr.markForCheck();
            })
        );
        this.subs.add(
            this.ttService.elapsedSeconds$.subscribe(secs => {
                if (this.isTimerOnThisTask) {
                    const m = Math.floor(secs / 60);
                    const s = secs % 60;
                    this.elapsed = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
                    this.cdr.markForCheck();
                }
            })
        );
        this.subs.add(
            this.dataService.timerStopped.subscribe(() => {
                this.loadEntries();
            })
        );
        // Refresh when an entry is added / edited / deleted from the see-all overlay.
        this.subs.add(
            this.dataService.refreshTaskTimeEntries.subscribe((taskId: number) => {
                if (taskId === this.taskId) { this.loadEntries(); }
            })
        );
        if (this.taskId) { this.loadEntries(); }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.taskId && !changes.taskId.firstChange) {
            this.loadEntries();
        }
    }

    loadEntries(): void {
        if (!this.taskId) return;
        this.isLoading = true;
        this.cdr.markForCheck();
        this.ttService.getTaskTimeEntries(this.taskId).subscribe(entries => {
            this.entries = entries || [];
            this.isLoading = false;
            this.cdr.markForCheck();
        });
    }

    get isTimerOnThisTask(): boolean {
        return !!(this.activeTimer && this.activeTimer.taskId === this.taskId);
    }

    get totalTime(): string {
        return this.ttService.formatTotalMinutes(this.entries);
    }

    get hasEntries(): boolean {
        return this.entries.length > 0;
    }

    startTimer(): void {
        const prev = this.ttService.activeTimer;
        this.ttService.startTimer(this.taskId, this.taskName, this.projectId).subscribe(() => {
            if (prev) {
                this.dataService.timerAutoStopped.next(prev);
            }
            this.cdr.markForCheck();
        });
    }

    stopTimer(): void {
        if (this.isStopping) return;
        this.isStopping = true;
        this.ttService.stopTimer().subscribe({
            next: () => {
                this.dataService.timerStopped.next(null);
                this.isStopping = false;
                this.loadEntries();
            },
            error: () => {
                this.isStopping = false;
                this.cdr.markForCheck();
            }
        });
    }

    toggleExpand(): void {
        this.isExpanded = !this.isExpanded;
    }

    onSeeAll(): void {
        this.openOverlay.emit();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }
}
